/**
 * Uma entrega de EPI: o funcionário, o que saiu (com o C.A. do dia) e a situação da assinatura.
 * O acompanhamento fino da assinatura — quem abriu o link, reenvio, cancelamento — mora na tela
 * de Assinaturas, para onde esta leva.
 */

import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { Download as DownloadIcon, Draw as DrawIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

import { DataTable, PageShell, type DataTableColumn } from '../../../components';
import { pode } from '../../../authorization/pode';
import { useControle } from '../../../authorization/useVeredito';
import entregaDeEpiService from '../../../services/entregaDeEpiService';
import {
  COR_DA_ENTREGA,
  ROTULO_DA_ENTREGA,
  ROTULO_DO_TIPO,
  dataDaEntrega,
  type EntregaDeEpi,
  type ItemDeEntregaDeEpi,
} from '../../../types/entregaDeEpi';

const colunas: DataTableColumn<ItemDeEntregaDeEpi>[] = [
  { id: 'nome', label: 'EPI', primary: true, render: (i) => i.nomeDoEpi },
  { id: 'ca', label: 'Nº do C.A.', render: (i) => i.numeroDoCa },
  { id: 'validade', label: 'Validade do C.A.', hideOnMobile: true, render: (i) => dataDaEntrega(i.validadeDoCa) },
  { id: 'tamanho', label: 'Tamanho', hideOnMobile: true, render: (i) => i.tamanho ?? '—' },
  { id: 'quantidade', label: 'Qtd.', align: 'right', render: (i) => i.quantidade },
];

const EXPLICACAO: Partial<Record<EntregaDeEpi['status'], string>> = {
  aguardando_assinatura: 'O link foi enviado ao funcionário. A ficha assinada fica disponível assim que ele assinar.',
  recusada: 'O funcionário recusou a ficha. O motivo está na trilha da assinatura.',
  expirada: 'O prazo do link venceu. Reenvie o link pela tela da assinatura.',
  cancelada: 'A assinatura foi cancelada. Registre de novo se a entrega aconteceu.',
  sem_assinatura: 'Registrada sem assinatura eletrônica: baixe a ficha, imprima e colha a assinatura no papel.',
};

export const EntregaDeEpiDetailPage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const verAssinatura = useControle('/assinaturas', pode('assinatura.read'), 'Acompanhar assinatura');

  const [entrega, setEntrega] = useState<EntregaDeEpi | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    entregaDeEpiService
      .detalhar(Number(id))
      .then((dados) => {
        if (vivo) setEntrega(dados);
      })
      .catch((err: unknown) => {
        if (vivo) setErro(err instanceof Error ? err.message : 'Não foi possível carregar a entrega.');
      });
    return () => {
      vivo = false;
    };
  }, [id]);

  const baixar = async (): Promise<void> => {
    if (!entrega) return;
    try {
      const nome = `Ficha de EPI - ${entrega.funcionario?.name ?? entrega.employeeId} - ${entrega.entregueEm}.pdf`;
      await entregaDeEpiService.baixarFicha(entrega.id, nome);
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível baixar a ficha.');
    }
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'EPIs', path: '/epis' },
        { label: 'Entregas', path: '/epis/entregas' },
        { label: entrega?.funcionario?.name ?? 'Entrega' },
      ]}
      title={entrega ? `${ROTULO_DO_TIPO[entrega.tipo]} de EPI — ${entrega.funcionario?.name ?? ''}` : 'Entrega de EPI'}
      subtitle={entrega ? `Em ${dataDaEntrega(entrega.entregueEm)}` : undefined}
      actions={
        entrega && (
          <Stack direction="row" spacing={1}>
            {entrega.documentoId !== null && (
              <Button
                variant="outlined"
                startIcon={<DrawIcon />}
                disabled={verAssinatura.disabled}
                onClick={() => navigate(`/assinaturas/${String(entrega.documentoId)}`)}
              >
                Acompanhar assinatura
              </Button>
            )}
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => void baixar()}>
              {entrega.status === 'assinada' ? 'Ficha assinada' : 'Ficha'}
            </Button>
          </Stack>
        )
      }
    >
      {erro && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>
          {erro}
        </Alert>
      )}

      {entrega && (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: EXPLICACAO[entrega.status] ? 1 : 0 }}>
              <Chip color={COR_DA_ENTREGA[entrega.status]} label={ROTULO_DA_ENTREGA[entrega.status]} />
              {entrega.funcionario?.employeeCode && (
                <Typography variant="body2" color="text.secondary">
                  Matrícula {entrega.funcionario.employeeCode}
                </Typography>
              )}
            </Stack>
            {EXPLICACAO[entrega.status] && (
              <Typography variant="body2" color="text.secondary">
                {EXPLICACAO[entrega.status]}
              </Typography>
            )}
            {entrega.observacao && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Observação
                </Typography>
                <Typography variant="body2">{entrega.observacao}</Typography>
              </Box>
            )}
          </Paper>

          <DataTable
            columns={colunas}
            rows={entrega.itens}
            getRowId={(i) => i.id}
            emptyState={{
              title: 'Esta entrega não tem EPIs',
              description: 'Toda entrega nasce com ao menos um EPI. Se a lista está vazia, o registro foi alterado fora do sistema.',
            }}
          />
        </>
      )}
    </PageShell>
  );
};

export default EntregaDeEpiDetailPage;
